// components/ExperienceManager.js
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  BorderColor,
  DeleteForever,
} from '@mui/icons-material';
import ExperienceForm from './ExperienceForm';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import moment from 'moment';
import CommonButton from '../../common/components/CommonButton';
import theme from '../../theme';

const ExperienceManager = ({
    experiences,
    setExperiences,
    editPersonalProfile
}) => {
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleAdd = () => {
    setSelectedExperience(null);
    setFormOpen(true);
  };

  const handleEdit = (experience) => {
    setSelectedExperience(experience);
    setFormOpen(true);
  };

  const handleDeleteClick = (experience) => {
    setSelectedExperience(experience);
    setDeleteDialogOpen(true);
  };

  const handleSave = (experienceData) => {
    if (selectedExperience) {
      setExperiences(prev => prev.map(exp => 
        exp.id === selectedExperience.id 
          ? { ...experienceData, id: selectedExperience.id }
          : exp
      ));
    } else {
      const newExperience = {
        ...experienceData,
        id: Date.now()
      };
      setExperiences(prev => [...prev, newExperience]);
    }
    setFormOpen(false);
    setSelectedExperience(null);
  };

  const handleDelete = () => {
    setExperiences(prev => prev.filter(exp => exp.id !== selectedExperience.id));
    setDeleteDialogOpen(false);
    setSelectedExperience(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDateRange = (startDate, endDate, currentJob) => {
    const start = moment(startDate).format('MMM yyyy');
    const end = currentJob ? 'Present' : (endDate ? moment(endDate).format('MMM yyyy') : '');
    return `${start} - ${end}`;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontSize={18} fontWeight={600}>
            Work Experience
          </Typography>
        </Box>
        <CommonButton 
           label="Add Experience"
           startIcon={<AddIcon />}
           background={theme.palette.secondary.main}
           color="White"
           width="170px"
           handleClick={handleAdd}
           marginTop="0px"
           marginBottom="0px"
           disabled={!editPersonalProfile}
        />
      </Box>

      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'secondary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Title</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Company</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Period</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {experiences
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((exp) => (
                <TableRow 
                  key={exp.id}
                  hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    <Typography variant="body1" fontWeight="medium">
                      {exp.title}
                    </Typography>
                  </TableCell>
                  <TableCell>{exp.company}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {formatDateRange(exp.startDate, exp.endDate, exp.currentJob)}
                      </Typography>
                      {exp.currentJob && (
                        <Chip 
                          label="Current" 
                          size="small" 
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {exp.currentJob ? (
                      <Chip label="Active" size="small" color="success" />
                    ) : (
                      <Chip label="Completed" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEdit(exp)} 
                        color="secondary"
                        sx={{ mr: 1 }}
                        disabled={!editPersonalProfile}
                      >
                        <BorderColor fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteClick(exp)} 
                        color="error"
                        disabled={!editPersonalProfile}
                      >
                        <DeleteForever fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {experiences.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      No experiences added yet. Click "Add Experience" to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={experiences.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Edit Form Dialog */}
      <Dialog 
        open={formOpen} 
        onClose={() => setFormOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
         <Typography fontSize={18} fontWeight={600}>
          {selectedExperience ? 'Edit Experience' : 'Add New Experience'}
         </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <ExperienceForm
            experience={selectedExperience}
            onSave={handleSave}
            onCancel={() => setFormOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        experienceTitle={selectedExperience?.title}
      />
    </Container>
  );
};

export default ExperienceManager;